<template>
    <div class="lesson-view dashboard-theme">
        <!-- 顶部导航 -->
        <van-nav-bar :title="linkExpired ? linkNavTitle : '课程详情'" :left-text="canGoBack ? '返回' : ''"
            :left-arrow="canGoBack" @click-left="goBack" class="dash-navbar" />

        <!-- 链接不可用：旧格式（/lesson/数字）且未登录，或分享码失效 -->
        <div v-if="linkExpired" class="link-expired">
            <van-icon name="info-o" size="46" color="#c0c6d4" />
            <p class="link-expired-title">{{ linkTitle }}</p>
            <p class="link-expired-desc">{{ linkMessage }}</p>
            <van-button round type="primary" class="link-expired-btn" @click="goLogin">
                去登录
            </van-button>
        </div>

        <div v-else class="dash-container">
            <!-- 家长确认查看：倒计时结束自动确认，家长也可以随时点按钮提前确认。
                 倒计时在切到后台时暂停，避免开着一半就关掉也被算作已查看。
                 老师预览链接时加 ?preview=1，不计时不自动标记。 -->
            <div v-if="isParentView" class="review-confirm" :class="{ 'review-confirm--done': parentViewed }">
                <template v-if="parentViewed">
                    <van-icon name="success" size="16" color="#07c160" />
                    <span class="review-confirm-text">欢迎回来，本节反馈你已查看过</span>
                </template>
                <template v-else-if="previewMode">
                    <van-icon name="eye-o" size="16" color="#9ca3af" />
                    <span class="review-confirm-text">预览模式：不会记为已查看</span>
                </template>
                <template v-else-if="reviewCountdown !== null">
                    <van-icon name="eye-o" size="16" color="#2563eb" />
                    <span class="review-confirm-text">阅读完后点一下，或等倒计时结束自动确认</span>
                    <button class="review-confirm-btn" :disabled="submittingReview" @click="confirmNow">
                        {{ submittingReview ? '确认中…' : (reviewCountdown > 0 ? `我已阅读（${reviewCountdown}s）` : '我已阅读') }}
                    </button>
                </template>
                <span v-else class="review-confirm-text">页面加载中…</span>
            </div>

            <!-- 概览区：左学生信息卡片 + 右评分卡片 -->
            <div class="overview-row">
                <!-- 学生信息卡片（按你刚修改的结构） -->
                <div class="overview-card overview-card--left overview-inner--left">
                    <div class="overview-inner">
                        <div class="overview-label">学生</div>
                        <div class="overview-name">{{ course.username || '--' }}</div>
                    </div>
                </div>

                <!-- 综合评分卡片 -->
                <div class="overview-card overview-card--right">
                    <div class="overview-inner rating-inner">
                        <div class="overview-label overview-label--dark">综合评分</div>

                        <div class="score-wrap">
                            <div class="score-sub">
                                {{ isPastTime ? '本节课综合评分' : '本节课未开始' }}
                            </div>
                        </div>

                        <div class="score-extra" v-if="isPastTime">
                            <van-rate v-if="course.rate" v-model="course.rate" :size="18" color="#2563eb"
                                void-color="#e5edff" :count="5" readonly />
                            <div class="score-text score-text--muted" v-else>
                                老师暂未填写评分
                            </div>
                        </div>

                        <div class="score-extra" v-else>
                            <div class="score-text score-text--muted">
                                上课结束后将展示本节课评分
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 上课时间独立信息条 -->
            <div class="time-strip">
                <div class="time-label">上课时间</div>
                <div class="time-value">{{ course.lesson_time || '--' }}</div>
            </div>

            <!-- 老师反馈标题：两边线 + 中间标题，对称布局 -->
            <div class="feedback-title-bar">
                <span class="feedback-line"></span>
                <span class="feedback-title-text">老师反馈</span>
                <span class="feedback-line"></span>
            </div>

            <!-- 老师反馈卡片 -->
            <div class="feedback-panel">
                <!-- 状态文案：浅色、居中 -->
                <div class="feedback-status">
                    <span v-if="isPastTime && course.feedback">
                        本节课老师已完成反馈
                    </span>
                    <span v-else-if="isPastTime && !course.feedback">
                        老师正在撰写本节课反馈
                    </span>
                    <span v-else>
                        等待本节课上完后展示反馈
                    </span>
                </div>

                <!-- 实际反馈内容 -->
                <div v-if="isPastTime && course.feedback" class="feedback-body" v-html="course.feedback"></div>

                <div v-else-if="isPastTime && !course.feedback" class="feedback-placeholder">
                    老师正在撰写本节课的反馈，请稍后查看。
                </div>

                <div v-else class="feedback-placeholder">
                    本节课尚未开始，上课结束后可查看老师评分与反馈。
                </div>
            </div>

            <!-- AI 建议板块：上课已开始且老师已反馈后展示 -->
            <div v-if="isPastTime && course.feedback" class="ai-section">
                <!-- AI 标题栏 -->
                <div class="feedback-title-bar">
                    <span class="feedback-line"></span>
                    <span class="feedback-title-text ai-title-text">✨ AI 智能建议</span>
                    <span class="feedback-line"></span>
                </div>

                <!-- AI 建议内容卡片 -->
                <div class="feedback-panel ai-panel">
                    <div class="feedback-status">
                        AI 根据本节课表现生成的练习指南
                    </div>

                    <!-- 模型署名：生成完成且已知模型名时展示 -->
                    <div v-if="aiState === 'done' && modelLabel" class="ai-model-credit">
                        来自：{{ modelLabel }}
                    </div>

                    <!-- 思考过程：折叠展示，用户可点开看进度 -->
                    <div v-if="aiReasoning" class="ai-reasoning">
                        <div class="ai-reasoning-header" @click="showReasoning = !showReasoning">
                            <span class="ai-reasoning-title">
                                <van-icon :name="showReasoning ? 'arrow-up' : 'arrow-down'" size="13" />
                                AI 思考过程
                            </span>
                            <span class="ai-reasoning-toggle">
                                {{ aiState === 'loading' ? '思考中…' : (showReasoning ? '收起' : '展开') }}
                            </span>
                        </div>
                        <div v-show="showReasoning" class="ai-reasoning-body">{{ aiReasoning }}</div>
                    </div>

                    <!-- 加载中：等待首个片段 -->
                    <div v-if="aiState === 'loading'" class="ai-loading">
                        <van-loading size="18px" color="#4f46e5">AI 正在分析本节课…</van-loading>
                    </div>

                    <!-- 出错 -->
                    <div v-else-if="aiState === 'error'" class="feedback-placeholder ai-error">
                        {{ aiError }}
                    </div>

                    <!-- 流式 / 完成：渲染 Markdown，流式时末尾追加光标 -->
                    <div v-else class="feedback-body ai-markdown-body"
                        :class="{ 'ai-streaming': aiState === 'streaming' }" v-html="renderMarkdown(aiFeedback)"></div>

                    <!-- 重试：仅在完成或出错时可点，重新走一遍生成流程 -->
                    <div v-if="aiState === 'done' || aiState === 'error'" class="ai-retry-bar">
                        <button type="button" class="ai-retry-btn" @click="startAiFeedback">
                            <span class="ai-retry-icon">↻</span> 重新生成
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Toast } from "vant";
import moment from "moment";
import axios from "axios";
import { marked } from "marked"; // 1. 引入 marked
// AI 反馈是"外部输入"（家长链接是公开的，任何人拿到链接都能写），
// marked 会把原始 HTML 和 [x](javascript:...) 原样输出，v-html 会执行。
// 渲染前统一用 DOMPurify 清洗。
import DOMPurify from "dompurify";

// ===== 大模型配置 =====
// 前端只负责指定模型名称，URL 与 KEY 由 nginx 代理层配置。
// 使用无思考的 deepseek-chat，输出更快、内容更稳定；需要换模型只改这一行即可。
// 留空("")则回退后端 ai-prompt 返回的默认模型。
const AI_MODEL = "deepseek-chat";

// 家长自动确认「已查看」的倒计时秒数
const REVIEW_SECONDS = 10;

/** 链接不可用时的默认说明（旧格式链接、或分享码失效） */
const LINK_EXPIRED_TEXT =
    "链接的格式更新过了，麻烦让老师重新发一次。\n如果你是学生，也可以登录后查看自己的课程。";

export default {
    name: "LessonView",
    data() {
        return {
            courseId: null,
            course: {},
            canGoBack: false,
            aiState: "idle",   // idle | loading | streaming | done | error
            aiFeedback: "",    // 累加的 Markdown 文本（最终内容）
            aiError: "",       // 错误提示
            usedModel: "",     // 本次实际调用的模型名，用于底部署名
            aiReasoning: "",   // 推理阶段缓存（折叠展示，让用户看到进度）
            enteredContent: false, // 是否已进入正式内容输出阶段
            showReasoning: false, // 思考过程折叠/展开
            isParentView: false,  // 是否家长分享链接访问
            submittingReview: false, // 正在上报「已查看」
            reviewCountdown: null, // 自动确认剩余秒数，null=未开始
            previewMode: false,    // 老师预览（?preview=1），不计时、不标记
            parentViewed: false,   // 是否已记录为已查看
            shareToken: null,      // 家长链接里的分享码（/share/:token）
            linkExpired: false,    // 链接不可用（旧格式且未登录 / 分享码无效 / 登录过期）
            linkTitle: "这个链接已经不能直接打开了",
            linkNavTitle: "链接已更新",
            linkMessage: ""        // 不可用时的说明文案
        };
    },
    computed: {
        ...mapGetters(["username", "token"]),

        isPastTime() {
            if (!this.course.lesson_time) return false;
            const m = moment(this.course.lesson_time, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"]);
            if (!m.isValid()) return false;
            return m.isBefore(moment());
        },

        // 底部署名展示的模型名：本次实际调用的优先，缓存直出时回退前端配置
        modelLabel() {
            return this.usedModel || AI_MODEL || "";
        }
    },
    beforeRouteEnter(to, from, next) {
        // 有真实站内来源才显示返回按钮；直接打开/外部进入则隐藏（避免返回白屏）
        next((vm) => {
            vm.canGoBack = !!(from && from.matched && from.matched.length);
        });
    },
    created() {
        // 两种进入方式：
        //   /share/:token       ── 家长链接，公开，地址栏里没有课程 ID
        //   /lesson/:id         ── 学生从课程列表点进来，需要登录
        this.shareToken = this.$route.params.token || null;
        this.courseId = this.$route.params.id || null;
        // 只有 /share/:token 才是家长模式。
        // 以前还看 ?user=parents，但那个参数既冗余又能随手伪造，已经去掉。
        this.isParentView = !!this.shareToken;

        // 老师自己点开链接预览时加 ?preview=1：页面照常展示，但不计时、
        // 不会把「已查看」记到家长头上。家长链接本身不带这个参数。
        this.previewMode = this.isParentView &&
            ["1", "true"].includes(String(this.$route.query.preview || ""));

        // 切到后台暂停倒计时，回前台继续 —— 否则家长点开就锁屏也会被算已读
        if (this.isParentView && !this.previewMode) {
            document.addEventListener("visibilitychange", this.onVisibilityChange);
        }

        // 旧格式链接（/lesson/2729）且没登录：**不能直接把 ID 对应的反馈给出去**，
        // 否则改地址栏数字就能翻别人家孩子的记录。这里给一页提示，
        // 而不是甩一个登录页 —— 家长没有账号，看到登录页只会来问老师。
        if (!this.shareToken && !this.token) {
            this.expireLink(LINK_EXPIRED_TEXT);
            return;
        }

        this.fetchCourseDetails();
    },
    beforeDestroy() {
        this.clearCountdownTimer();
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
    },
    methods: {
        /**
         * 拼这节课相关接口的地址
         *
         * 家长模式走 /api/lesson-view/{token}（公开、不出现课程 ID），
         * 学生模式走 /api/lesson/{id}（带学生自己的令牌）。
         * 两边的 review / ai-prompt / ai-feedback 都要跟着走 ——
         * 家长没有令牌，走 /api/lesson/... 会被鉴权拦掉。
         *
         * ⚠️ 注意别搞混：家长在地址栏里看到的是「/share/{token}」（前端路由），
         * 这里是它背后调的接口路径，两者不是同一个词。
         */
        lessonApi(suffix = "") {
            return this.shareToken
                ? `/api/lesson-view/${this.shareToken}${suffix}`
                : `/api/lesson/${this.courseId}${suffix}`;
        },
        fetchCourseDetails() {
            axios
                .get(this.lessonApi(), {
                    headers: {
                        'Authorization': `Bearer ${this.token}`
                    }
                })
                .then(response => {
                    if (response.data && response.data.data) {
                        this.course = response.data.data;
                        // ★ 分享码模式下，真正的课程 ID 是从这里拿到的 ——
                        //   后面的 /review、/ai-prompt 都要用它
                        this.courseId = this.course.id;

                        // 家长端：已经确认过就显示"已查看"；
                        // 没确认过且不是预览模式，就开始倒计时，到点自动确认。
                        if (this.isParentView) {
                            if (this.course.review_time) {
                                this.parentViewed = true;
                            } else if (!this.previewMode) {
                                this.startReviewCountdown();
                            }
                        }

                        // 上课已开始且老师已反馈，才需要 AI 建议
                        if (this.isPastTime && this.course.feedback) {
                            const cached = this.course.ai_feedback;
                            if (cached && cached.trim()) {
                                // 有缓存：直接渲染，不再调用大模型
                                this.aiFeedback = cached;
                                this.aiState = "done";
                            } else {
                                // 无缓存：走生成流程
                                this.startAiFeedback();
                            }
                        }
                    } else if (this.shareToken) {
                        // 后端明确回了 error=1：分享码无效、或那节课已经不存在
                        this.expireLink(LINK_EXPIRED_TEXT);
                    } else {
                        Toast("获取课程详情失败");
                    }
                })
                .catch(error => {
                    console.error("API 请求失败:", error);

                    if (this.shareToken) {
                        // 网络/服务端出错和"链接失效"不是一回事，分开说，
                        // 否则会把家长引去让老师重发一条其实没问题的链接
                        this.expireLink("页面没能加载出来，请检查网络后重试。");

                        return;
                    }

                    // 本地还留着登录态、但服务器不认了（学生令牌过期）。
                    // 这种情况报"请检查网络"会让人白查半天网络 —— 直说是登录过期。
                    if (error.response && error.response.status === 401) {
                        this.$store.dispatch("setToken", "");
                        this.expireLink(
                            "登录已失效，请重新登录后再查看。",
                            "登录已失效",
                            "登录已失效"
                        );

                        return;
                    }

                    Toast("请求失败，请检查网络");
                });
        },
        async startAiFeedback() {
            this.aiState = "loading";
            this.aiFeedback = "";
            this.aiReasoning = "";
            this.enteredContent = false;
            this.aiError = "";
            // 生成开始时自动展开思考区，让用户实时看到进度
            this.showReasoning = true;
            try {
                // 1. 向后端取整理好的提示词
                const promptResp = await axios.get(this.lessonApi("/ai-prompt"), {
                    headers: { Authorization: `Bearer ${this.token}` }
                });
                const payload = promptResp.data && promptResp.data.data;
                if (!payload || !payload.messages) {
                    this.aiError = "AI 提示词获取失败";
                    this.aiState = "error";
                    return;
                }

                // 2. 带上 stream:true，请求大模型代理拿 SSE 流
                await this.streamChat(payload);
            } catch (e) {
                console.error("AI 反馈失败:", e);
                this.aiError = "AI 建议获取失败，请稍后重试";
                this.aiState = "error";
            }
        },

        async streamChat(payload) {
            // 前端指定的模型优先，未指定时用后端返回的默认模型
            const model = AI_MODEL || payload.model;
            this.usedModel = model;
            // 使用后端 ai-prompt 返回的 messages / model / temperature / max_tokens
            const resp = await fetch("/ai/chat", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    messages: payload.messages,
                    model,
                    // 内容稳定性：temperature/top_p 越低，每次输出差异越小
                    // 想调回后端配置值时把 temperature 改回 payload.temperature 即可
                    temperature: 0.2,
                    top_p: 0.3,
                    // reasoning 模型推理会占用 token，给足额度避免内容被截断
                    max_tokens: Math.max(payload.max_tokens || 0, 4096),
                    stream: true
                })
            });

            if (!resp.ok || !resp.body) {
                this.aiError = "AI 服务响应异常";
                this.aiState = "error";
                return;
            }

            const reader = resp.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                // 统一换行：nginx/CDN 可能把 \n\n 规范化为 \r\n\r\n，先归一再切分
                buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");

                // OpenAI SSE 以 \n\n 分隔每条消息，保留末尾不完整片段
                const parts = buffer.split("\n\n");
                buffer = parts.pop();

                for (const part of parts) {
                    const finished = this.handleChatEvent(part);
                    if (finished) {
                        try { reader.cancel(); } catch (e) { /* ignore */ }
                        this.finalizeAi();
                        return;
                    }
                }
            }
            this.finalizeAi();
        },

        // 处理单条 SSE 消息，返回 true 表示收到 [DONE]
        handleChatEvent(rawEvent) {
            const lines = rawEvent.split("\n");
            for (const line of lines) {
                const trimmed = line.trimStart();
                if (!trimmed.startsWith("data:")) continue;

                const data = trimmed.slice(5).trim();
                if (!data) continue;
                if (data === "[DONE]") return true;

                try {
                    const obj = JSON.parse(data);
                    const choice = obj.choices && obj.choices[0];
                    const delta = choice && choice.delta;
                    if (!delta) return false;

                    // reasoning 与 content 独立累积，互不截断
                    if (delta.reasoning_content) {
                        this.aiReasoning += delta.reasoning_content;
                    }
                    if (delta.content) {
                        this.enteredContent = true;
                        this.aiFeedback += delta.content; // 原样拼接，不 trim
                        this.aiState = "streaming";
                    }
                } catch (e) {
                    console.warn("无法解析 SSE 数据:", data);
                }
            }
            return false;
        },

        finalizeAi() {
            if (this.aiFeedback) {
                // 有正式正文：正常完成，自动收起思考区突出正文
                this.aiState = "done";
                if (this.enteredContent) this.showReasoning = false;
                this.saveAiFeedback(this.aiFeedback);
            } else if (this.aiReasoning) {
                // 全程没有 content：该模型把内容放在 reasoning 里，兜底展示
                this.aiFeedback = this.aiReasoning;
                this.aiState = "done";
                this.saveAiFeedback(this.aiFeedback);
            } else if (this.aiState !== "error") {
                this.aiError = "AI 未返回内容，请稍后重试";
                this.aiState = "error";
            }
        },

        // 回存 AI 反馈。若后端返回 error=1（如已存在），静默忽略
        saveAiFeedback(text) {
            axios
                .post(
                    this.lessonApi("/ai-feedback"),
                    { ai_feedback: text },
                    { headers: { Authorization: `Bearer ${this.token}` } }
                )
                .catch(e => {
                    console.warn("AI 反馈保存失败（忽略）:", e);
                });
        },

        renderMarkdown(text) {
            if (!text) return "";
            // 先转 Markdown，再消毒：
            //   - 去掉 <script>、onerror 等原始 HTML
            //   - 去掉 javascript:/data: 等危险链接
            // 已经存在库里的旧内容也会在这里被一起清掉
            return DOMPurify.sanitize(
                marked.parse(text, {
                    breaks: true // 开启回车换行
                })
            );
        },
        // ===== 家长确认「已查看」 =====
        // 两种触发：倒计时到 0 自动上报；家长提前读完可点按钮立即上报。
        // 切到后台会暂停计时（见 onVisibilityChange），避免打开就放着也算已读。
        startReviewCountdown() {
            if (!this.isParentView || this.parentViewed || this.previewMode) return;
            if (this.reviewCountdown === null) this.reviewCountdown = REVIEW_SECONDS;
            this.clearCountdownTimer();
            this._reviewTimer = setInterval(() => {
                if (this.reviewCountdown > 1) {
                    this.reviewCountdown -= 1;
                } else {
                    this.reviewCountdown = 0;
                    this.clearCountdownTimer();
                    this.confirmNow();
                }
            }, 1000);
        },
        clearCountdownTimer() {
            if (this._reviewTimer) {
                clearInterval(this._reviewTimer);
                this._reviewTimer = null;
            }
        },
        confirmNow() {
            if (this.parentViewed || this.submittingReview) return;
            // 手动提前确认或自动到点，都先停掉计时，避免重复触发
            this.clearCountdownTimer();
            this.submittingReview = true;
            this.reportViewed();
        },
        reportViewed() {
            if (this.parentViewed) return;
            axios
                .get(this.lessonApi("/review"))
                .then(() => {
                    this.parentViewed = true;
                    this.submittingReview = false;
                    this.reviewCountdown = null;
                    Toast.success("已确认查看");
                })
                .catch((error) => {
                    // 失败不自动重试，按钮停在「我已阅读」让家长手动再点一次
                    this.submittingReview = false;
                    console.warn("确认查看上报失败:", error);
                    Toast("确认失败，请稍后再试");
                });
        },
        // 切后台暂停倒计时，回到前台继续
        onVisibilityChange() {
            if (!this.isParentView || this.parentViewed || this.previewMode) return;
            if (document.hidden) {
                this.clearCountdownTimer();
            } else if (!this._reviewTimer && this.reviewCountdown !== null && this.reviewCountdown > 0) {
                this.startReviewCountdown();
            }
        },
        /**
         * 把页面切成"打不开"状态
         *
         * 链接失效和登录过期都走这儿，但**说法要分开** ——
         * 一个该让老师重发链接，一个该让用户重新登录，混着说会把人指错方向。
         *
         * @param {string} message  正文说明
         * @param {string} title    页面标题
         * @param {string} navTitle 顶栏标题（要短，长了会挤）
         */
        expireLink(message, title = "这个链接已经不能直接打开了", navTitle = "链接已更新") {
            this.linkTitle = title;
            this.linkNavTitle = navTitle;
            this.linkMessage = message;
            this.linkExpired = true;
        },
        goLogin() {
            this.$router.replace("/login");
        },
        goBack() {
            if (this.canGoBack) {
                this.$router.back();
            } else {
                this.$router.replace("/");
            }
        }
    }
};
</script>

<style scoped>
/* 背景 */
.dashboard-theme {
    min-height: 100vh;
    background:
        radial-gradient(circle at 0 0, #e4edff 0, transparent 45%),
        radial-gradient(circle at 100% 0, #fdecc8 0, transparent 45%),
        linear-gradient(to bottom, #f3f4fb 0, #f9fafb 100%);
    padding-bottom: 24px;
    box-sizing: border-box;
}

/* 链接不可用时的提示页（旧格式链接 / 分享码失效） */
.link-expired {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 64px 32px 0;
    text-align: center;
}

.link-expired-title {
    margin: 18px 0 0;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
}

.link-expired-desc {
    margin: 10px 0 0;
    font-size: 13px;
    line-height: 1.8;
    color: #6b7280;
    white-space: pre-line; /* 文案里的 \n 要换行 */
}

/* Vant 2 的默认主色是绿色（#07c160），跟站点的蓝不是一套，
   这里按登录页那颗按钮的样式覆盖一下 */
.link-expired-btn {
    margin-top: 24px;
    min-width: 150px;
    height: 44px;
    border: none;
    font-size: 15px;
    font-weight: 500;
    background-image: linear-gradient(135deg, #2550ff, #4f7dff);
    box-shadow: 0 12px 26px rgba(129, 140, 248, 0.45);
}

/* 导航栏 */
.dash-navbar {
    background-color: transparent;
}

.dash-navbar>>>.van-nav-bar__title {
    color: #111827;
    font-weight: 600;
    font-size: 18px;
}

.dash-navbar>>>.van-icon,
.dash-navbar>>>.van-nav-bar__text {
    color: #4b5563;
    font-size: 15px;
}

/* 容器 */
.dash-container {
    padding: 14px 18px 0;
    box-sizing: border-box;
}

/* 家长确认已查看横幅 */
.review-confirm {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 12px;
    padding: 10px 12px;
    border-radius: 12px;
    background: #eef4ff;
    border: 1px solid #d3e3ff;
    color: #2563eb;
    font-size: 13px;
    box-sizing: border-box;
}

.review-confirm--done {
    background: #e6f4ea;
    border-color: #c6e6d3;
    color: #188038;
}

.review-confirm-text {
    flex: 1;
    line-height: 1.5;
    text-align: left;
}

.review-confirm-btn {
    flex-shrink: 0;
    width: 122px;
    box-sizing: border-box;
    border: none;
    border-radius: 999px;
    padding: 6px 0;
    text-align: center;
    background: #2563eb;
    color: #ffffff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
}

.review-confirm-btn:disabled {
    opacity: 0.6;
    cursor: default;
}

/* 概览行 */
.overview-row {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
}

/* 概览卡片 */
.overview-card {
    position: relative;
    flex: 1;
    min-width: 0;
    border-radius: 20px;
    background: #ffffff;
    box-shadow:
        0 16px 36px rgba(148, 163, 184, 0.32),
        0 0 0 1px rgba(226, 232, 240, 0.9);
    overflow: hidden;
}

.overview-card--left {
    flex: 1.1;
}

.overview-card--right {
    flex: 1;
}

.overview-inner {
    position: relative;
    box-sizing: border-box;
}

/* 左侧姓名卡片：整块渐变 */
.overview-inner--left {
    padding: 16px 14px 14px;
    background: linear-gradient(135deg, #818cf8, #6366f1);
    text-align: left;
}

.overview-inner--left .overview-inner {
    padding: 0;
}

/* 文本样式 */
.overview-label {
    font-size: 13px;
    color: rgba(249, 250, 251, 0.95);
    margin-bottom: 4px;
}

.overview-label--dark {
    font-size: 13px;
    color: #4b5563;
    margin-bottom: 6px;
}

.overview-name {
    font-size: 20px;
    font-weight: 600;
    color: #ffffff;
}

/* 上课时间条 */
.time-strip {
    margin: 0 2px 14px;
    padding: 12px 14px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.24);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    box-sizing: border-box;
}

.time-label {
    font-size: 13px;
    color: #6b7280;
    margin-bottom: 4px;
}

.time-value {
    font-size: 15px;
    font-weight: 500;
    color: #111827;
}

/* 右侧评分卡片 */
.overview-card--right {
    background: linear-gradient(to bottom, #eff6ff 0, #ffffff 35%, #ffffff 100%);
}

.rating-inner {
    padding: 16px 14px 14px;
    box-sizing: border-box;
    text-align: center;
}

.score-wrap {
    margin-top: 4px;
}

.score-sub {
    font-size: 14px;
    color: #374151;
}

/* 星星稍小，避免折行 */
.score-extra {
    margin-top: 10px;
}

.score-text {
    margin-top: 4px;
    font-size: 13px;
    color: #4b5563;
}

.score-text--muted {
    color: #9ca3af;
}

/* 老师反馈标题：对称的线 + 文本 */
.feedback-title-bar {
    margin: 10px 0 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.feedback-line {
    flex: 1;
    height: 1px;
    background: linear-gradient(to right, transparent, #d1d5db, transparent);
}

.feedback-title-text {
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
}

/* 老师反馈卡片 */
.feedback-panel {
    background: #ffffff;
    border-radius: 20px;
    padding: 14px 16px 18px;
    box-shadow:
        0 14px 34px rgba(148, 163, 184, 0.3),
        0 0 0 1px rgba(226, 232, 240, 0.9);
    box-sizing: border-box;
}

/* 状态文案：浅色、居中 */
.feedback-status {
    text-align: center;
    font-size: 13px;
    color: #9ca3af;
    margin-bottom: 10px;
}

/* 实际反馈内容：字体大一点 */
.feedback-body {
    font-size: 15px;
    line-height: 1.9;
    color: #111827;
    text-align: left;
}

/* v-html 的 p 间距 */
.feedback-body :deep(p) {
    margin: 6px 0;
}

/* 占位文本 */
.feedback-placeholder {
    font-size: 14px;
    line-height: 1.8;
    color: #6b7280;
    text-align: left;
}

/* 小屏适配 */
@media (max-width: 375px) {
    .dash-container {
        padding: 12px 14px 0;
    }

    .overview-row {
        flex-direction: column;
        gap: 10px;
    }

    .overview-card {
        border-radius: 18px;
    }

    .feedback-panel {
        padding: 12px 14px 16px;
        border-radius: 18px;
    }
}

/* AI 专属样式 */
/* AI 整体块，与上面的老师反馈保持节奏 */
.ai-section {
    margin-top: 18px;
}

/* 标题稍微强调一点，但不抢眼 */
.ai-title-text {
    font-weight: 600;
    letter-spacing: 0.02em;
    color: #4f46e5;
}

/* AI 卡片：干净的长文阅读卡片 */
.ai-panel {
    margin-top: 4px;
    border-radius: 16px;
    border: 1px solid rgba(148, 163, 184, 0.4);
    background-color: #ffffff;
    box-shadow:
        0 10px 24px rgba(148, 163, 184, 0.22),
        0 0 0 1px rgba(229, 231, 235, 0.9);
}

/* 顶部说明文案轻一点 */
.ai-panel .feedback-status {
    font-size: 12px;
    color: #9ca3af;
    margin-bottom: 6px;
}

/* ===== Markdown 长文阅读优化 ===== */

.ai-markdown-body {
    font-size: 14px;
    line-height: 1.9;
    color: #111827;
    padding-top: 4px;
}

/* 段落：在这类文案里更像“小标题+一句解释”，稍微明显一点 */
.ai-markdown-body :deep(p) {
    margin: 8px 0;
    word-break: break-word;
    font-weight: 400;
    color: #4b5563;
}

/* 连续 <br> 不要拉太大行高 */
.ai-markdown-body :deep(br + br) {
    line-height: 0;
}

/* 标题（如果大模型输出了 # 语法） */
/* 小标题：深紫实底 + 白色粗体，最醒目的块状标题
   （实色块 + 白字，任何设备字重渲染都一致） */
.ai-markdown-body :deep(h1),
.ai-markdown-body :deep(h2),
.ai-markdown-body :deep(h3) {
    margin: 18px 0 10px;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.5;
    background: linear-gradient(135deg, #4f46e5 0%, #4338ca 100%);
    border-radius: 6px;
    padding: 8px 12px;
    box-shadow: 0 2px 6px rgba(79, 70, 229, 0.25);
}

.ai-markdown-body :deep(h1) {
    font-size: 17px;
}

.ai-markdown-body :deep(h2) {
    font-size: 16px;
}

.ai-markdown-body :deep(h3) {
    font-size: 15px;
}

/* 分割线（示例中的 ---------------------------） */
.ai-markdown-body :deep(hr) {
    border: none;
    border-top: 1px dashed #e5e7eb;
    margin: 14px 0;
}

/* 列表整体缩进：基础缩进，适合手机阅读 */
.ai-markdown-body :deep(ul),
.ai-markdown-body :deep(ol) {
    padding-left: 1em;
    margin: 6px 0 8px;
}

/* 有序列表：作为“主要步骤/建议”，整体更醒目一点 */
.ai-markdown-body :deep(ol) {
    padding-left: 1.2em;
    /* 比 ul 略多一点缩进 */
}

.ai-markdown-body :deep(ol li) {
    margin-bottom: 6px;
    color: #111827;
    font-weight: 500;
    /* 稍加粗，步骤更明显 */
}

/* 有序列表数字更亮一点 */
.ai-markdown-body :deep(ol li)::marker {
    color: #4f46e5;
    font-weight: 700;
}

/* 无序列表：作为“补充说明/可观察点” */
.ai-markdown-body :deep(ul li) {
    margin-bottom: 4px;
    color: #4b5563;
    font-style: normal;
}

/* 无序列表的小圆点柔和一点 */
.ai-markdown-body :deep(ul li)::marker {
    color: #9ca3af;
}

/* 列表内的段落再细调一下间距 */
.ai-markdown-body :deep(li p) {
    margin: 3px 0;
}

/* ul 里的重点词保留直立 */
.ai-markdown-body :deep(ul li strong) {
    font-style: normal;
    font-weight: 600;
    color: #111827;
}

/* 全局加粗：用于关键词/小标题，浅琥珀高亮背景增强存在感 */
.ai-markdown-body :deep(strong) {
    font-weight: 600;
    color: #111827;
    background-color: #ffe9a8;
    padding: 0 3px;
    border-radius: 4px;
}

/* 引用块（如果 AI 用 > 语法）：用于特别提醒 */
.ai-markdown-body :deep(blockquote) {
    margin: 8px 0;
    padding: 8px 10px;
    border-left: 3px solid #e5e7eb;
    background-color: #f9fafb;
    border-radius: 6px;
    color: #4b5563;
}

/* 行内 code（防御性处理） */
.ai-markdown-body :deep(code) {
    font-size: 12px;
    background-color: #f3f4f6;
    padding: 1px 3px;
    border-radius: 3px;
}

/* 链接（如果以后有） */
.ai-markdown-body :deep(a) {
    color: #2563eb;
    text-decoration: none;
    border-bottom: 1px dashed rgba(37, 99, 235, 0.6);
}

.ai-markdown-body :deep(a:hover) {
    border-bottom-style: solid;
}

/* AI 加载中 */
.ai-loading {
    display: flex;
    justify-content: center;
    padding: 12px 0 4px;
}

/* AI 错误提示 */
.ai-error {
    color: #b45309;
    text-align: center;
}

/* 模型署名：居中灰色小字 */
.ai-model-credit {
    margin: 2px 0 10px;
    text-align: center;
    font-size: 11px;
    letter-spacing: 0.02em;
    color: #9ca3af;
}

/* 思考过程折叠区 */
.ai-reasoning {
    margin: 0 0 8px;
    border: 1px solid rgba(148, 163, 184, 0.25);
    border-radius: 10px;
    background: #f9fafb;
    overflow: hidden;
}

.ai-reasoning-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 10px;
    font-size: 12px;
    cursor: pointer;
    user-select: none;
}

.ai-reasoning-title {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #9ca3af;
}

.ai-reasoning-toggle {
    color: #4f46e5;
    font-weight: 500;
}

.ai-reasoning-body {
    max-height: 180px;
    overflow-y: auto;
    padding: 0 10px 8px;
    font-size: 12px;
    line-height: 1.6;
    color: #9ca3af;
    white-space: pre-wrap;
    word-break: break-word;
    text-align: left;
}

/* 重试按钮区 */
.ai-retry-bar {
    display: flex;
    justify-content: center;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid rgba(148, 163, 184, 0.2);
}

.ai-retry-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 20px;
    font-size: 13px;
    font-weight: 600;
    color: #4f46e5;
    background-color: #eef2ff;
    border: 1px solid rgba(79, 70, 229, 0.25);
    border-radius: 999px;
    cursor: pointer;
    transition: background-color 0.15s, transform 0.1s;
}

.ai-retry-btn:hover {
    background-color: #e0e7ff;
}

.ai-retry-btn:active {
    transform: scale(0.96);
}

.ai-retry-icon {
    font-size: 15px;
    line-height: 1;
}

/* 流式打字机光标：末尾闪烁竖线 */
.ai-markdown-body.ai-streaming :deep(> :last-child)::after {
    content: "▍";
    display: inline;
    margin-left: 1px;
    color: #4f46e5;
    animation: ai-caret-blink 1s step-end infinite;
}

@keyframes ai-caret-blink {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0;
    }
}
</style>
